import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, Chip, Alert, Skeleton,
  Button, Tooltip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MonitorHeart as MonitorHeartIcon,
  Timeline as TimelineIcon,
  ArrowUpward, ArrowDownward,
  TrendingUp, TrendingDown,
  WarningAmber,
  AccountBalance, Business, Groups,
  BarChart as BarChartIcon,
  SwapVert,
} from '@mui/icons-material';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Line, LineChart,
  Area, AreaChart, ReferenceLine,
} from 'recharts';
import { concentrationAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS — colours only (labels come from i18n)
   ═══════════════════════════════════════════════════════════════ */
const REGIME_COLORS = {
  RISK_OFF: '#d32f2f', RECOVERY_MIXED: '#e65100', RECOVERY_CONFIRMED: '#ef6c00',
  NARROW_RALLY: '#1565c0', RISK_ON: '#2e7d32', OVERHEAT: '#7b1fa2', ROTATION: '#00838f',
};
const LIGHT_CLR = { GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336' };
const LIGHT_KEYS = ['light_trend', 'light_breadth', 'light_concentration'];
const LIGHT_TYPE_MAP = { light_trend: 'trend', light_breadth: 'breadth', light_concentration: 'concentration' };
const REGIME_CLR = {
  RISK_OFF: '#d32f2f', RECOVERY_MIXED: '#ff9800', RECOVERY_CONFIRMED: '#fb8c00',
  NARROW_RALLY: '#1976d2', RISK_ON: '#4caf50', OVERHEAT: '#9c27b0', ROTATION: '#00838f',
};

/* Format VND values (foreign flow is raw VND, ~100 billion = 100 tỷ) */
const fmtVnd = (v) => {
  if (!v) return '0';
  const abs = Math.abs(v);
  const sign = v >= 0 ? '+' : '';
  if (abs >= 1e12) return `${sign}${(v / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}${(v / 1e9).toFixed(0)}B`;
  if (abs >= 1e6) return `${sign}${(v / 1e6).toFixed(0)}M`;
  return `${sign}${v.toFixed(0)}`;
};

/* Shared card style */
const CARD_SX = { boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.02)', border: '1px solid #e2e8f0', borderRadius: 1.5 };
const CARD_HEADER_SX = { px: 2, py: 1, borderBottom: '1px solid #f1f5f9', bgcolor: 'rgba(0,0,0,0.015)' };

/* ═══════════════════════════════════════════════════════════════
   HERO CARD — Regime status, allocation, gates, action notes
   Layout: accent bar → status bar → allocation → notes → pulse
   ═══════════════════════════════════════════════════════════════ */
const HeroCard = ({ data, regimeDays, prevRegime }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const r = data.regime || 'RECOVERY_MIXED';
  const mColor = REGIME_COLORS[r] || REGIME_COLORS.RECOVERY_MIXED;
  const regimeTitle = t(`concentrationAllocation.regime.${r}`);
  const cash = (data.cash_pct ?? 0) * 100;
  const short_ = (data.short_pct ?? 0) * 100;
  const long_ = (data.long_pct ?? 0) * 100;
  const exp = ((data.exposure ?? 0) * 100).toFixed(0);

  // Resolve action_note_keys → i18n strings
  const noteKeys = Array.from(new Set(data.action_note_keys || []));
  const rotCtx = data.rotation_ctx || {};
  const cleanNotes = noteKeys
    .filter(k => k !== 'traffic_summary')
    .map(k => {
      if (k === 'regime_narrow_detail') {
        return t(`concentrationAllocation.actionNotes.${k}`, {
          dom5: ((data.dom5 ?? 0) * 100).toFixed(1) + '%',
          breadth_c: ((data.breadth_c_score ?? 0) * 100).toFixed(1) + '%',
        });
      }
      if (k === 'rotation_summary') {
        return t(`concentrationAllocation.actionNotes.${k}`, {
          churn: ((rotCtx.churn ?? 0) * 100).toFixed(0) + '%',
          leaders: rotCtx.leaders || 'N/A',
        });
      }
      return t(`concentrationAllocation.actionNotes.${k}`);
    })
    .filter(Boolean);

  // Regime description from i18n (bilingual)
  const regimeDesc = t(`concentrationAllocation.regimeDesc.${r}`);

  const expNum = Number(exp);
  const gaugeColor = expNum > 70 ? '#10b981' : expNum > 40 ? '#f59e0b' : '#ef4444';

  const gates = [
    { key: 'bluechip', open: data.gate_a_open ?? data.long_tier1_open },
    { key: 'midcap', open: data.gate_b_open ?? data.long_tier2_open },
    { key: 'penny', open: data.gate_c_open ?? false },
  ];

  return (
    <Card elevation={0} sx={{
      mb: 2.5, borderRadius: 1.5, overflow: 'hidden', border: 'none',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 16px rgba(0,0,0,0.03)',
    }}>
      {/* ─── Regime accent strip ─── */}
      <Box sx={{ height: 3, background: `linear-gradient(90deg, ${mColor}, ${mColor}60)` }} />

      {/* ═══ STATUS BAR: Regime + VNI | Traffic Lights ═══ */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 1.25, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: mColor, flexShrink: 0 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: mColor, whiteSpace: 'nowrap' }}>
              {regimeTitle}
            </Typography>
          </Box>
          {regimeDays > 0 && (
            <Typography sx={{
              fontSize: '0.62rem', fontWeight: 700, color: mColor, opacity: 0.8,
              bgcolor: `${mColor}10`, px: 0.75, py: 0.15, borderRadius: 1, whiteSpace: 'nowrap',
            }}>
              {t('concentrationAllocation.regime.dayCount', { days: regimeDays })}
            </Typography>
          )}
          {data.vnindex && (
            <>
              <Box sx={{ width: '1px', height: 18, bgcolor: '#cbd5e1', mx: 0.25, flexShrink: 0 }} />
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>VNI</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, fontFamily: 'monospace' }}>
                  {data.vnindex.close?.toLocaleString('en', { maximumFractionDigits: 1 })}
                </Typography>
                <Typography sx={{
                  fontSize: '0.8rem', fontWeight: 700, fontFamily: 'monospace',
                  color: (data.vnindex.change_pct ?? 0) >= 0 ? '#10b981' : '#ef4444',
                }}>
                  {(data.vnindex.change_pct ?? 0) >= 0 ? '+' : ''}{data.vnindex.change_pct?.toFixed(2)}%
                </Typography>
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {LIGHT_KEYS.map(k => {
            const v = data[k] || 'RED';
            const lightType = LIGHT_TYPE_MAP[k];
            return (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LIGHT_CLR[v], flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
                  {t(`concentrationAllocation.trafficLight.${lightType}`)}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: LIGHT_CLR[v] }}>
                  {t(`concentrationAllocation.trafficLight.${v}_${lightType}`)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ═══ ALLOCATION SECTION — 2-column ═══ */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        {/* Regime description */}
        <Typography sx={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: 600, mb: 1.5, lineHeight: 1.5 }}>
          {regimeDesc}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Left column — exposure + bar + legend */}
          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {/* Exposure + Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0 }}>
                <Typography sx={{
                  fontSize: '1.8rem', fontWeight: 900, color: gaugeColor,
                  lineHeight: 1, fontFamily: 'monospace', letterSpacing: -1,
                }}>
                  {exp}%
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8' }}>
                  {t('concentrationAllocation.allocation.exposure')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Box sx={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
                  {long_ > 0 && <Box sx={{ width: `${long_}%`, bgcolor: '#10b981', transition: 'width 0.6s ease' }} />}
                  {short_ > 0 && <Box sx={{ width: `${short_}%`, bgcolor: '#f59e0b', transition: 'width 0.6s ease' }} />}
                  <Box sx={{ flex: 1 }} />
                </Box>
              </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                { key: 'long', value: long_, color: '#10b981' },
                { key: 'short', value: short_, color: '#f59e0b' },
                { key: 'cash', value: cash, color: '#94a3b8' },
              ].filter(x => x.value > 0).map(x => (
                <Box key={x.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: x.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                    {t(`concentrationAllocation.allocation.${x.key}`)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: x.color, fontFamily: 'monospace' }}>
                    {x.value.toFixed(0)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right column — gates + notes */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 auto' }, minWidth: { md: 280 }, display: 'flex', flexDirection: 'column' }}>
            {/* Gate chips */}
            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
              {gates.map(g => (
                <Chip key={g.key} size="small"
                  icon={<Box sx={{ width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: g.open ? '#10b981' : 'transparent', border: g.open ? 'none' : '1px solid #d1d5db', ml: 0.5 }}>
                    {g.open && <Typography sx={{ fontSize: '0.55rem', color: '#fff', lineHeight: 1 }}>✓</Typography>}
                  </Box>}
                  label={`${t(`concentrationAllocation.gates.${g.key}`)}: ${g.open ? t('concentrationAllocation.gates.canBuy') : t('concentrationAllocation.gates.wait')}`}
                  sx={{
                    height: 26, fontSize: '0.7rem', fontWeight: 600, borderRadius: '6px',
                    bgcolor: g.open ? '#f0fdf4' : '#fafafa',
                    color: g.open ? '#047857' : '#9ca3af',
                    border: `1px solid ${g.open ? '#bbf7d0' : '#e5e7eb'}`,
                    '& .MuiChip-label': { px: 0.75 },
                    '& .MuiChip-icon': { mr: -0.25 },
                  }}
                />
              ))}
            </Box>

            {/* Action notes */}
            {cleanNotes.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {cleanNotes.map((line, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#cbd5e1', mt: '6px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.5, color: '#334155' }}>
                      {line}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ═══ MARKET PULSE ═══ */}
      {(() => {
        const fn = data.market_foreign_net;
        const vr = data.volume_ratio;
        const ew = data.ew_market != null ? (data.ew_market * 100) : null;
        const capw = data.capw_market != null ? (data.capw_market * 100) : null;
        const isIllusory = capw != null && ew != null && capw > 0 && ew <= 0;
        const isBroadStrength = capw != null && ew != null && ew > 0 && capw <= 0;
        if (fn == null && vr == null && capw == null) return null;
        return (
          <Box sx={{
            px: 2.5, py: 0.75, borderTop: '1px solid #f1f5f9',
            display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'nowrap', overflowX: 'auto',
          }}>
            {fn != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.25 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {t('concentrationAllocation.foreign.label')}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                  color: fn >= 0 ? '#10b981' : '#ef4444' }}>
                  {fmtVnd(fn)}
                </Typography>
              </Box>
            )}
            {vr != null && (
              <>
                {fn != null && <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0', flexShrink: 0 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.25 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {t('concentrationAllocation.volume.label')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                    color: vr >= 1.2 ? '#10b981' : vr <= 0.8 ? '#ef4444' : '#f59e0b' }}>
                    {vr.toFixed(2)}x
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600,
                    color: vr >= 1.2 ? '#10b981' : vr <= 0.8 ? '#ef4444' : '#f59e0b' }}>
                    {vr >= 1.2 ? t('concentrationAllocation.volume.high')
                      : vr <= 0.8 ? t('concentrationAllocation.volume.low')
                      : t('concentrationAllocation.volume.normal')}
                  </Typography>
                </Box>
              </>
            )}
            {capw != null && ew != null && (
              <>
                <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0', flexShrink: 0 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.25 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {t('concentrationAllocation.reality.indexReturn')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                    color: capw >= 0 ? '#10b981' : '#ef4444' }}>
                    {capw >= 0 ? '+' : ''}{capw.toFixed(2)}%
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mx: 0.25 }}>vs</Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {t('concentrationAllocation.reality.realReturn')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                    color: ew >= 0 ? '#10b981' : '#ef4444' }}>
                    {ew >= 0 ? '+' : ''}{ew.toFixed(2)}%
                  </Typography>
                </Box>
              </>
            )}
            {(isIllusory || isBroadStrength) && (
              <>
                <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap', px: 1.25,
                  color: isIllusory ? '#e65100' : '#059669' }}>
                  {isIllusory
                    ? t('concentrationAllocation.reality.illusory')
                    : t('concentrationAllocation.reality.broadStrength')}
                </Typography>
              </>
            )}
          </Box>
        );
      })()}
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SCORE STRIP — 5 key metrics inline (thin bar)
   Fields: trend_score, breadth_c_score, dom5, heat, exposure
   ═══════════════════════════════════════════════════════════════ */
const ScoreStrip = ({ data, prevData }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const s = (k) => t(`concentrationAllocation.score.${k}`);
  const metrics = [
    { label: s('trend'), desc: s('trendDesc'), key: 'trend_score', invertDelta: false,
      value: (data.trend_score ?? 0) * 100, color: '#3b82f6',
      tag: v => v >= 60 ? [s('strong'),'#10b981'] : v >= 40 ? [s('average'),'#f59e0b'] : [s('weak'),'#ef4444'] },
    { label: s('breadth'), desc: s('breadthDesc'), key: 'breadth_c_score', invertDelta: false,
      value: (data.breadth_c_score ?? 0) * 100, color: '#10b981',
      tag: v => v >= 55 ? [s('wide'),'#10b981'] : v >= 40 ? [s('average'),'#f59e0b'] : [s('narrow'),'#ef4444'] },
    { label: s('concentration'), desc: s('concentrationDesc'), key: 'dom5', invertDelta: true,
      value: (data.dom5 ?? 0) * 100, color: '#f59e0b',
      tag: v => v >= 55 ? [s('high'),'#ef4444'] : v >= 35 ? [s('average'),'#f59e0b'] : [s('dispersed'),'#10b981'] },
    { label: s('heat'), desc: s('heatDesc'), key: 'heat', invertDelta: true,
      value: (data.heat ?? 0) * 100, color: '#ef4444',
      tag: v => v >= 65 ? [s('hot'),'#ef4444'] : v >= 35 ? [s('warm'),'#f59e0b'] : [s('cold'),'#3b82f6'] },
  ];

  return (
    <Card elevation={0} sx={{ mb: 2.5, ...CARD_SX }}>
      <CardContent sx={{ py: 1.75, px: 2.5, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', gap: 3.5 }}>
          {metrics.map(m => {
            const val = Math.min(Math.max(m.value, 0), 100);
            const [tagText, tagClr] = m.tag(val);
            const prevVal = prevData ? (prevData[m.key] ?? 0) * 100 : null;
            const delta = prevVal !== null ? val - prevVal : null;
            const showDelta = delta !== null && Math.abs(delta) >= 0.5;
            const deltaClr = showDelta ? (m.invertDelta ? (delta > 0 ? '#ef4444' : '#10b981') : (delta > 0 ? '#10b981' : '#ef4444')) : null;
            return (
              <Box key={m.label} sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>{m.label}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    {showDelta && <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: deltaClr }}>{delta > 0 ? '▲' : '▼'}{Math.abs(delta).toFixed(1)}</Typography>}
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: tagClr, fontFamily: 'monospace' }}>{val.toFixed(0)}%</Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: tagClr }}>{tagText}</Typography>
                  </Box>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9' }}>
                  <Box sx={{ height: '100%', width: `${val}%`, bgcolor: tagClr, borderRadius: 3, transition: 'width 0.6s ease' }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   BUCKET CARD — Nhóm Cổ Phiếu A / B / C
   
   Layout: 3 HÀNG NGANG (không phải 3 cột dọc)
   Mỗi hàng: Icon+Name | AD counts | MA20 bar | MA50 bar | Flow
   
   Fields: bucket_a/b/c.{count, advancers, decliners, flat,
           pct_above_ma20, pct_above_ma50, money_flow, ret_ew}
           + is_narrow_rally, total_stocks
   ═══════════════════════════════════════════════════════════════ */
const MiniBar = ({ value, width = 80 }) => {
  const pct = Math.min(Math.max((value ?? 0) * 100, 0), 100);
  const color = pct >= 50 ? '#10b981' : pct >= 35 ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width, height: 5, borderRadius: 2.5, bgcolor: '#f1f5f9', flexShrink: 0 }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 2.5, transition: 'width 0.5s' }} />
      </Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', minWidth: 28 }}>
        {pct.toFixed(0)}%
      </Typography>
    </Box>
  );
};

const BucketCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const indexChangePct = typeof data.vnindex?.change_pct === 'number'
    ? data.vnindex.change_pct
    : (typeof data.capw_market === 'number' ? data.capw_market * 100 : 0);
  const narrowMoveKey = indexChangePct >= 0 ? 'narrowRallyUp' : 'narrowRallyDown';

  const buckets = [
    { key: 'bucket_a', labelKey: 'bucketA', Icon: AccountBalance },
    { key: 'bucket_b', labelKey: 'bucketB', Icon: Business },
    { key: 'bucket_c', labelKey: 'bucketC', Icon: Groups },
  ];

  return (
    <Card elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...CARD_SX }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ ...CARD_HEADER_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BarChartIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{t('concentrationAllocation.bucket.title')}</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
            {t('concentrationAllocation.bucket.totalStocks', { count: data.total_stocks })}
          </Typography>
        </Box>

        {/* Column headers */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
          <Box sx={{ width: 130 }} />
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', width: 60, textAlign: 'right' }}>{t('concentrationAllocation.bucket.returnEw')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: 90 }}>
            <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>{t('concentrationAllocation.bucket.advDecl')}</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', width: 110 }}>MA20</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', width: 110 }}>MA50</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', flex: 1, textAlign: 'right' }}>{t('concentrationAllocation.bucket.foreignNet')}</Typography>
        </Box>

        {/* Bucket rows */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {buckets.map((b, idx) => {
            const d = data[b.key];
            if (!d) return null;
            return (
              <Box key={b.key} sx={{
                flex: 1, display: 'flex', alignItems: 'center', px: 2, py: 1,
                borderBottom: idx < 2 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' },
              }}>
                {/* Name */}
                <Box sx={{ width: 130 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <b.Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                      {t(`concentrationAllocation.bucket.${b.labelKey}`)} ({d.count})
                    </Typography>
                  </Box>
                </Box>
                {/* Return EW */}
                <Box sx={{ width: 60, textAlign: 'right' }}>
                  <Typography sx={{
                    fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace',
                    color: (d.ret_ew ?? 0) >= 0 ? '#10b981' : '#ef4444',
                  }}>
                    {(d.ret_ew ?? 0) >= 0 ? '+' : ''}{((d.ret_ew ?? 0) * 100).toFixed(2)}%
                  </Typography>
                </Box>
                {/* AD */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: 90 }}>
                  <ArrowUpward sx={{ fontSize: 11, color: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981' }}>{d.advancers}</Typography>
                  <ArrowDownward sx={{ fontSize: 11, color: '#ef4444' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#ef4444' }}>{d.decliners}</Typography>
                </Box>
                {/* MA20 */}
                <Box sx={{ width: 110 }}>
                  <MiniBar value={d.pct_above_ma20} />
                </Box>
                {/* MA50 */}
                <Box sx={{ width: 110 }}>
                  <MiniBar value={d.pct_above_ma50} />
                </Box>
                {/* Foreign Net */}
                <Box sx={{ flex: 1, textAlign: 'right' }}>
                  <Typography sx={{
                    fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace',
                    color: (d.foreign_net ?? 0) >= 0 ? '#10b981' : '#ef4444',
                  }}>
                    {fmtVnd(d.foreign_net ?? 0)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Narrow rally warning */}
        {data.is_narrow_rally && (
          <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff3e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningAmber sx={{ fontSize: 14, color: '#e65100' }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#e65100' }}>
                {t(`concentrationAllocation.bucket.${narrowMoveKey}`)}
              </Typography>
            </Box>
          </Box>
        )}
        {/* A vs C divergence insight */}
        {data.bucket_a && data.bucket_c && (() => {
          const aRet = (data.bucket_a.ret_ew ?? 0) * 100;
          const cRet = (data.bucket_c.ret_ew ?? 0) * 100;
          const aUp = aRet > 0 && cRet < 0;
          const cUp = cRet > 0 && aRet < 0;
          if (aUp) return (
            <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff8e1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmber sx={{ fontSize: 14, color: '#f57f17' }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#f57f17' }}>
                  {t('concentrationAllocation.bucket.divergenceAUp', {
                    aRet: aRet.toFixed(2), cRet: cRet.toFixed(2),
                  })}
                </Typography>
              </Box>
            </Box>
          );
          if (cUp) return (
            <Box sx={{ px: 2, py: 0.75, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#e8f5e9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUp sx={{ fontSize: 14, color: '#2e7d32' }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#2e7d32' }}>
                  {t('concentrationAllocation.bucket.divergenceCUp', {
                    aRet: aRet.toFixed(2), cRet: cRet.toFixed(2),
                  })}
                </Typography>
              </Box>
            </Box>
          );
          return null;
        })()}
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   CONTRIBUTORS — Ai đang kéo thị trường?

   Layout: 2 bảng cạnh nhau (Kéo TĂNG | Kéo GIẢM)
   Mỗi bên: 5 rows — # | Mã | Ngành | %1D | Điểm
   
   Fields: top_pull/top_push[].{ticker, return_1d, contrib_points, sector_code}
   ═══════════════════════════════════════════════════════════════ */
const ContributorsCard = ({ data }) => {
  const { t, i18n } = useTranslation();
  if (!data) return null;
  const pull = (data.top_pull || []).slice(0, 8);
  const push = (data.top_push || []).slice(0, 8);

  const ContribList = ({ items, type }) => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {type === 'up' ? <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} /> : <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: type === 'up' ? '#10b981' : '#ef4444' }}>
          {type === 'up' ? t('concentrationAllocation.contributors.pullUp') : t('concentrationAllocation.contributors.pushDown')}
        </Typography>
      </Box>
      {/* Table header */}
      <Box sx={{ display: 'flex', px: 2, py: 0.3, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', width: 24 }}>{t('concentrationAllocation.contributors.rank')}</Typography>
        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', flex: 1 }}>{t('concentrationAllocation.contributors.ticker')}</Typography>
        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', flex: '0 0 64px', textAlign: 'right' }}>{t('concentrationAllocation.contributors.sector')}</Typography>
        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', flex: '0 0 56px', textAlign: 'right' }}>{t('concentrationAllocation.contributors.pct1d')}</Typography>
        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', flex: '0 0 64px', textAlign: 'right' }}>{t('concentrationAllocation.contributors.points')}</Typography>
      </Box>
      {items.length === 0 ? (
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>{t('concentrationAllocation.contributors.noData')}</Typography>
        </Box>
      ) : items.map((item, i) => (
        <Box key={i} sx={{
          display: 'flex', alignItems: 'center', px: 2, py: 0.5, flex: 1,
          borderBottom: i < items.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
        }}>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', width: 24 }}>{i + 1}</Typography>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, flex: 1 }}>{item.ticker}</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', flex: '0 0 64px', textAlign: 'right' }}>
            {(i18n.language === 'vi' ? item.sector_name?.vi : item.sector_name?.en) || '—'}
          </Typography>
          <Typography sx={{
            fontSize: '0.7rem', fontFamily: 'monospace', flex: '0 0 56px', textAlign: 'right',
            color: (item.return_1d ?? 0) >= 0 ? '#10b981' : '#ef4444',
          }}>
            {((item.return_1d ?? 0) * 100).toFixed(1)}%
          </Typography>
          <Typography sx={{
            fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
            color: (item.contrib_points ?? 0) >= 0 ? '#10b981' : '#ef4444',
            flex: '0 0 64px', textAlign: 'right',
          }}>
            {(item.contrib_points ?? 0) > 0 ? '+' : ''}{(item.contrib_points ?? 0).toFixed(2)}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Card elevation={0} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', ...CARD_SX }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ ...CARD_HEADER_SX, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <SwapVert sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{t('concentrationAllocation.contributors.title')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <ContribList items={pull} type="up" />
          <Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.06)' }} />
          <ContribList items={push} type="down" />
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ALLOCATION CHART — Lịch sử phân bổ (stacked area)

   Fields: history[].{long_pct, short_pct, cash_pct, regime, dom5}
   ═══════════════════════════════════════════════════════════════ */
const AllocationChart = ({ history }) => {
  const { t } = useTranslation();
  if (!history?.length) return null;
  const chartData = [...history].reverse().map(d => ({
    date: d.trade_date,
    long: (d.long_pct ?? 0) * 100,
    short: (d.short_pct ?? 0) * 100,
    cash: (d.cash_pct ?? 0) * 100,
    regime: d.regime,
    dom5: (d.dom5 ?? 0) * 100,
  }));

  // Find regime transition dates
  const regimeChanges = chartData.reduce((acc, d, i) => {
    if (i > 0 && d.regime !== chartData[i - 1].regime) {
      acc.push({ date: d.date, from: chartData[i - 1].regime, to: d.regime });
    }
    return acc;
  }, []);

  const legendItems = [
    { key: 'long', c: '#10b981' }, { key: 'short', c: '#ef4444' },
    { key: 'cash', c: '#93c5fd' }, { key: 'concentration', c: '#f59e0b' },
  ];

  return (
    <Card elevation={0} sx={{ height: '100%', width: '100%', ...CARD_SX }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ ...CARD_HEADER_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <TimelineIcon sx={{ fontSize: '1rem' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{t('concentrationAllocation.chart.allocationTitle')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {legendItems.map(x => (
              <Box key={x.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Box sx={{ width: 12, height: 3, bgcolor: x.c, borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                  {t(`concentrationAllocation.chart.legend${x.key.charAt(0).toUpperCase() + x.key.slice(1)}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ px: 1, py: 1 }}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#999' }} angle={-45} textAnchor="end" height={45}
                tickFormatter={v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; }} />
              <YAxis tick={{ fontSize: 9, fill: '#999' }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              {regimeChanges.map((rc, i) => (
                <ReferenceLine key={`rc-${i}`} x={rc.date} stroke={REGIME_CLR[rc.to] || '#999'}
                  strokeDasharray="4 3" strokeWidth={1.5}
                  label={{ value: t(`concentrationAllocation.regime.${rc.to}`).slice(0, 10),
                    fill: REGIME_CLR[rc.to] || '#999', fontSize: 7, position: 'top' }} />
              ))}
              <RTooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const regime = chartData.find(d => d.date === label)?.regime;
                const rColor = REGIME_COLORS[regime];
                const rTitle = regime ? t(`concentrationAllocation.regime.${regime}`) : '';
                return (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: '0.72rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: 3 }}>{label}</div>
                    {rColor && <div style={{ marginBottom: 4, fontSize: '0.65rem', color: rColor, fontWeight: 600 }}>● {rTitle}</div>}
                    {payload.map(p => (
                      <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, marginBottom: 1 }}>
                        <span style={{ color: p.color }}>{p.name}</span>
                        <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{p.value?.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                );
              }} />
              <Area type="monotone" dataKey="long" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6}
                name={t('concentrationAllocation.chart.legendLong')} />
              <Area type="monotone" dataKey="short" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6}
                name={t('concentrationAllocation.chart.legendShort')} />
              <Area type="monotone" dataKey="cash" stackId="1" stroke="#93c5fd" fill="#93c5fd" fillOpacity={0.4}
                name={t('concentrationAllocation.chart.legendCash')} />
              <Line type="monotone" dataKey="dom5" stroke="#f59e0b" strokeWidth={2} dot={false}
                name={t('concentrationAllocation.chart.legendConcentration')} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        {/* Regime color strip */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', height: 12, borderRadius: 1.5, overflow: 'hidden', mb: 0.5 }}>
            {chartData.map((d, i) => (
              <Tooltip key={i} title={`${d.date}: ${t(`concentrationAllocation.regime.${d.regime}`)}`} arrow>
                <Box sx={{ flex: 1, bgcolor: REGIME_CLR[d.regime] || '#999', cursor: 'pointer', '&:hover': { filter: 'brightness(1.2)' } }} />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.keys(REGIME_COLORS).map(k => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: REGIME_CLR[k] }} />
                <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{t(`concentrationAllocation.regime.${k}`)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SIGNALS CHART — Biến động sức khoẻ (line)

   Fields: history[].{bucket_c_pct_above_ma20, dom5}
   ═══════════════════════════════════════════════════════════════ */
const SignalsChart = ({ history }) => {
  const { t } = useTranslation();
  if (!history?.length) return null;
  const chartData = [...history].reverse().map(d => ({
    date: d.trade_date,
    stocksRising: (d.bucket_c_pct_above_ma20 ?? 0) * 100,
    concentration: (d.dom5 ?? 0) * 100,
    exposure: (d.exposure ?? 0) * 100,
    trend: (d.trend_score ?? 0) * 100,
    vnindex: d.vnindex_close ?? null,
    regime: d.regime,
  }));
  const hasVnindex = chartData.some(d => d.vnindex != null);

  // Find regime transition dates
  const regimeChanges = chartData.reduce((acc, d, i) => {
    if (i > 0 && d.regime !== chartData[i - 1].regime) {
      acc.push({ date: d.date, from: chartData[i - 1].regime, to: d.regime });
    }
    return acc;
  }, []);

  const legendItems = [
    { key: 'legendStocksRising', c: '#10b981' }, { key: 'legendConcentrationPct', c: '#f59e0b' },
    { key: 'legendExposure', c: '#8b5cf6' }, { key: 'legendTrend', c: '#3b82f6' },
    ...(hasVnindex ? [{ key: 'legendVnindex', c: '#0f172a' }] : []),
  ];

  return (
    <Card elevation={0} sx={{ width: '100%', ...CARD_SX }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ ...CARD_HEADER_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <TimelineIcon sx={{ fontSize: '1rem' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{t('concentrationAllocation.chart.signalsTitle')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {legendItems.map(x => (
              <Box key={x.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Box sx={{ width: 12, height: 3, bgcolor: x.c, borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                  {t(`concentrationAllocation.chart.${x.key}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ px: 1, py: 1 }}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#999' }} angle={-45} textAnchor="end" height={45}
                tickFormatter={v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; }} />
              <YAxis yAxisId="left" tick={{ fontSize: 9, fill: '#999' }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              {hasVnindex && (
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: '#999' }}
                  domain={['auto', 'auto']} tickFormatter={v => v?.toFixed(0)} />
              )}
              {regimeChanges.map((rc, i) => (
                <ReferenceLine key={`rc-${i}`} yAxisId="left" x={rc.date} stroke={REGIME_CLR[rc.to] || '#999'}
                  strokeDasharray="4 3" strokeWidth={1.5} />
              ))}
              <RTooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: '0.72rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                    {payload.map(p => (
                      <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, marginBottom: 1 }}>
                        <span style={{ color: p.color }}>{p.name}</span>
                        <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {p.dataKey === 'vnindex' ? p.value?.toFixed(2) : `${p.value?.toFixed(1)}%`}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }} />
              <ReferenceLine yAxisId="left" y={55} stroke="#10b981" strokeDasharray="5 5"
                label={{ value: t('concentrationAllocation.chart.refHealthy'), fill: '#10b981', fontSize: 9, position: 'right' }} />
              <ReferenceLine yAxisId="left" y={40} stroke="#f59e0b" strokeDasharray="5 5"
                label={{ value: t('concentrationAllocation.chart.refWarning'), fill: '#f59e0b', fontSize: 9, position: 'right' }} />
              <Line yAxisId="left" type="monotone" dataKey="stocksRising" stroke="#10b981" strokeWidth={2} dot={false}
                name={t('concentrationAllocation.chart.legendStocksRising')} />
              <Line yAxisId="left" type="monotone" dataKey="concentration" stroke="#f59e0b" strokeWidth={2} dot={false}
                name={t('concentrationAllocation.chart.legendConcentrationPct')} />
              <Line yAxisId="left" type="monotone" dataKey="exposure" stroke="#8b5cf6" strokeWidth={2} dot={false}
                strokeDasharray="5 3"
                name={t('concentrationAllocation.chart.legendExposure')} />
              <Line yAxisId="left" type="monotone" dataKey="trend" stroke="#3b82f6" strokeWidth={2} dot={false}
                strokeDasharray="3 2"
                name={t('concentrationAllocation.chart.legendTrend')} />
              {hasVnindex && (
                <Line yAxisId="right" type="monotone" dataKey="vnindex" stroke="#0f172a" strokeWidth={2} dot={false}
                  connectNulls
                  name={t('concentrationAllocation.chart.legendVnindex')} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SECTOR STRIP — Sector contribution summary (compact horizontal)

   Shows which sectors drive the index — key for sector concentration risk.
   Fields: sector_summary[].{sector_code, sector_name, net_points, pull_count, push_count}
   ═══════════════════════════════════════════════════════════════ */
const SectorStrip = ({ data }) => {
  const { t, i18n } = useTranslation();
  if (!data?.sector_summary?.length) return null;

  const lang = i18n.language === 'vi' ? 'vi' : 'en';
  const labelOf = (s) => s.sector_name?.[lang] || s.sector_code;

  // Split into two groups: pulling up (>0) and dragging down (<0).
  // Sort by absolute impact so the biggest mover is first in each group.
  const sorted = [...data.sector_summary].sort(
    (a, b) => Math.abs(b.net_points) - Math.abs(a.net_points)
  );
  const pulling = sorted.filter((s) => s.net_points > 0).slice(0, 4);
  const dragging = sorted.filter((s) => s.net_points < 0).slice(0, 4);
  const maxAbs = Math.max(
    ...[...pulling, ...dragging].map((s) => Math.abs(s.net_points)),
    0.01
  );

  // Headline insight: which side dominates and what to do about it.
  const topPos = pulling[0];
  const topNeg = dragging[0];
  const posMag = topPos ? Math.abs(topPos.net_points) : 0;
  const negMag = topNeg ? Math.abs(topNeg.net_points) : 0;
  let insight = t('concentrationAllocation.sector.insight_balanced');
  let insightTone = '#64748b';
  if (negMag > posMag * 1.5 && topNeg) {
    insight = t('concentrationAllocation.sector.insight_dragged', {
      name: labelOf(topNeg),
      points: negMag.toFixed(1),
    });
    insightTone = '#ef4444';
  } else if (posMag > negMag * 1.5 && topPos) {
    insight = t('concentrationAllocation.sector.insight_pulled', {
      name: labelOf(topPos),
      points: posMag.toFixed(1),
    });
    insightTone = '#10b981';
  }

  const renderRow = (s, color) => {
    const barW = Math.min((Math.abs(s.net_points) / maxAbs) * 100, 100);
    const isPos = s.net_points >= 0;
    const up = s.pull_count ?? 0;
    const down = s.push_count ?? 0;
    const total = s.total_stocks ?? (up + down);
    return (
      <Box
        key={s.sector_code}
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          columnGap: 1,
          py: 0.4,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.25, gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#1e293b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              title={labelOf(s)}
            >
              {labelOf(s)}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 800,
                fontFamily: 'monospace',
                color,
                flexShrink: 0,
              }}
            >
              {isPos ? '+' : ''}{s.net_points.toFixed(1)}
            </Typography>
          </Box>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${barW}%`,
                bgcolor: color,
                borderRadius: 2,
                transition: 'width 0.4s',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mt: 0.3 }}>
            <Box component="span" sx={{ color: '#10b981', fontWeight: 700 }}>{up}</Box>
            {' '}{lang === 'vi' ? 'tăng' : 'up'} ·{' '}
            <Box component="span" sx={{ color: '#ef4444', fontWeight: 700 }}>{down}</Box>
            {' '}{lang === 'vi' ? 'giảm' : 'down'}
            {total > 0 && (
              <Box component="span" sx={{ color: '#cbd5e1', ml: 0.4 }}>
                / {total}
              </Box>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  const groupHeader = (label, color, count) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color, letterSpacing: 0.2, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', ml: 'auto' }}>
        {count}
      </Typography>
    </Box>
  );

  return (
    <Card elevation={0} sx={{ mb: 2, ...CARD_SX }}>
      <CardContent sx={{ py: 1.25, px: 2, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <Business sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
          <Tooltip
            arrow
            placement="top"
            title={t('concentrationAllocation.sector.titleTip')}
          >
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 700,
                borderBottom: '1px dotted #cbd5e1',
                cursor: 'help',
              }}
            >
              {t('concentrationAllocation.sector.title')}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', ml: 'auto' }}>
            {t('concentrationAllocation.sector.subtitle')}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '0.72rem',
            color: insightTone,
            fontWeight: 600,
            mb: 1,
            lineHeight: 1.45,
          }}
        >
          {insight}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 2.5,
            rowGap: 1,
          }}
        >
          {/* Pulling up */}
          <Box>
            {groupHeader(
              t('concentrationAllocation.sector.pulling'),
              '#10b981',
              pulling.length
            )}
            {pulling.length === 0 ? (
              <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', fontStyle: 'italic' }}>
                —
              </Typography>
            ) : (
              pulling.map((s) => renderRow(s, '#10b981'))
            )}
          </Box>

          {/* Dragging down */}
          <Box>
            {groupHeader(
              t('concentrationAllocation.sector.dragging'),
              '#ef4444',
              dragging.length
            )}
            {dragging.length === 0 ? (
              <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', fontStyle: 'italic' }}>
                —
              </Typography>
            ) : (
              dragging.map((s) => renderRow(s, '#ef4444'))
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE — Toàn bộ layout

   Row 1:  HeroCard            (full width — regime, allocation, notes, gates)
   Row 2:  ScoreStrip          (full width — 5 metrics)
   Row 3:  BucketCard 38% | ContributorsCard 62%  (cân bằng height)
   Row 4:  Chart header + toggle
   Row 5:  AllocationChart 62% | SignalsChart 38%

   API data mapping (100% coverage):
     allocation.{regime, regime_description, action_notes,
       cash_pct, short_pct, long_pct, exposure, short_share, long_share,
       trend_score, breadth_c_score, heat,
       long_tier1_open, long_tier2_open,
       traffic_lights.{trend, breadth, concentration}}
     concentration.{dom5, dom10, narrowness, ew_market, capw_market,
       is_narrow_rally, is_broad_rally, total_stocks,
       bucket_a/b/c.{count, advancers, decliners, flat,
         pct_above_ma20, pct_above_ma50, money_flow, ret_ew, ad_ratio},
       top_pull/push[].{ticker, return_1d, weight, contrib, contrib_points,
         close, volume, sector_code, market_cap_bn}}
   ═══════════════════════════════════════════════════════════════ */
export default function ConcentrationAllocation() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyDays, setHistoryDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reportRes, historyRes] = await Promise.all([
        concentrationAPI.getReport(),
        concentrationAPI.getHistory(historyDays),
      ]);
      if (reportRes.data?.success) setReport(reportRes.data);
      else setError(reportRes.data?.error || t('concentrationAllocation.errorNoData'));

      let historyData = historyRes.data?.data || [];
      if (historyRes.data?.success) {
        // Auto-trigger calculation if history is stale (latest date < today)
        const today = new Date().toISOString().slice(0, 10);
        const latestDate = historyData.length > 0 ? historyData[0].trade_date : null;
        const isWeekday = [1,2,3,4,5].includes(new Date().getDay());
        if (isWeekday && latestDate && latestDate < today) {
          setCalculating(true);
          try {
            await concentrationAPI.calculate();
            const refreshed = await concentrationAPI.getHistory(historyDays);
            if (refreshed.data?.success) historyData = refreshed.data.data || [];
          } catch (calcErr) {
            console.warn('Auto-calculate failed:', calcErr.message);
          } finally {
            setCalculating(false);
          }
        }
        setHistory(historyData);
      }
    } catch (err) {
      setError(err.message || t('concentrationAllocation.errorConnection'));
    } finally {
      setLoading(false);
    }
  }, [historyDays, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Flatten ALL API fields into one object — components pick what they need */
  const latest = report ? {
    regime: report.allocation?.regime,
    action_note_keys: report.allocation?.action_note_keys,
    rotation_ctx: report.allocation?.rotation_ctx,
    cash_pct: report.allocation?.cash_pct,
    short_pct: report.allocation?.short_pct,
    long_pct: report.allocation?.long_pct,
    exposure: report.allocation?.exposure,
    short_share: report.allocation?.short_share,
    long_share: report.allocation?.long_share,
    trend_score: report.allocation?.trend_score,
    breadth_c_score: report.allocation?.breadth_c_score,
    heat: report.allocation?.heat,
    long_tier1_open: report.allocation?.long_tier1_open,
    long_tier2_open: report.allocation?.long_tier2_open,
    light_trend: report.allocation?.traffic_lights?.trend,
    light_breadth: report.allocation?.traffic_lights?.breadth,
    light_concentration: report.allocation?.traffic_lights?.concentration,
    dom5: report.concentration?.dom5,
    dom10: report.concentration?.dom10,
    narrowness: report.concentration?.narrowness,
    ew_market: report.concentration?.ew_market,
    capw_market: report.concentration?.capw_market,
    is_narrow_rally: report.concentration?.is_narrow_rally,
    is_broad_rally: report.concentration?.is_broad_rally,
    total_stocks: report.concentration?.total_stocks,
    bucket_a: report.concentration?.bucket_a,
    bucket_b: report.concentration?.bucket_b,
    bucket_c: report.concentration?.bucket_c,
    top_pull: report.concentration?.top_pull,
    top_push: report.concentration?.top_push,
    sector_summary: report.concentration?.sector_summary,
    market_foreign_net: report.concentration?.market_foreign_net,
    volume_ratio: report.concentration?.volume_ratio,
    vnindex: report.vnindex,
  } : null;

  /* Compute previous-day data and regime duration from history */
  const prevData = history.length >= 2 ? history[1] : null;

  const regimeDays = (() => {
    if (!history.length || !latest) return 0;
    const curRegime = latest.regime;
    let count = 0;
    for (const h of history) {
      if (h.regime === curRegime) count++;
      else break;
    }
    return count;
  })();

  const prevRegime = (() => {
    if (!history.length || !latest) return null;
    const curRegime = latest.regime;
    for (const h of history) {
      if (h.regime !== curRegime) return h.regime;
    }
    return null;
  })();

  if (loading) {
    return (
      <Box sx={{ px: 2, py: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5 }} />
            <Box>
              <Skeleton width={260} height={24} />
              <Skeleton width={200} height={14} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: '4px' }} />
        </Box>

        {/* Hero Card — new vertical layout */}
        <Card elevation={0} sx={{ mb: 2.5, borderRadius: 1.5, overflow: 'hidden', ...CARD_SX }}>
          {/* Accent strip */}
          <Skeleton variant="rectangular" height={3} />
          {/* Status bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 1.25, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={10} height={10} />
              <Skeleton width={120} height={20} />
              <Skeleton width={60} height={14} />
              <Skeleton width={100} height={18} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="circular" width={8} height={8} />
                  <Skeleton width={50} height={13} />
                  <Skeleton width={40} height={15} />
                </Box>
              ))}
            </Box>
          </Box>
          {/* Allocation */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Skeleton width="60%" height={14} sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Skeleton width={70} height={36} />
              <Skeleton variant="rounded" height={14} sx={{ flex: 1, maxWidth: 400, borderRadius: 7 }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {[0, 1, 2].map(i => <Skeleton key={i} width={60} height={14} />)}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" width={80} height={24} sx={{ borderRadius: 3 }} />)}
              </Box>
            </Box>
          </Box>
          {/* Notes */}
          <Box sx={{ mx: 2, mb: 1.5, px: 2, py: 1.25, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
            {[0, 1].map(i => <Skeleton key={i} width={`${80 - i * 20}%`} height={14} sx={{ mb: 0.5 }} />)}
          </Box>
          {/* Pulse */}
          <Box sx={{ px: 2.5, py: 1, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 2 }}>
            {[0, 1, 2].map(i => <Skeleton key={i} width={80} height={14} />)}
          </Box>
        </Card>

        {/* Score Strip */}
        <Card elevation={0} sx={{ mb: 2.5, ...CARD_SX }}>
          <CardContent sx={{ py: 1.75, px: 2.5, '&:last-child': { pb: 1.75 } }}>
            <Box sx={{ display: 'flex', gap: 3.5 }}>
              {[0, 1, 2, 3].map(i => (
                <Box key={i} sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Skeleton width={50} height={13} />
                    <Skeleton width={40} height={16} />
                  </Box>
                  <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3 }} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Bucket + Contributors */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'stretch' }}>
          <Box sx={{ flex: '0 0 45%', minWidth: 0 }}>
            <Card elevation={0} sx={{ height: '100%', ...CARD_SX }}>
              <Box sx={{ ...CARD_HEADER_SX }}><Skeleton width={140} height={16} /></Box>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
                  <Box sx={{ width: 130, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Skeleton variant="circular" width={14} height={14} />
                    <Skeleton width={80} height={14} />
                  </Box>
                  <Skeleton width={40} height={12} sx={{ mx: 1 }} />
                  <Skeleton variant="rounded" width={80} height={5} sx={{ borderRadius: 2.5, mx: 1 }} />
                  <Skeleton variant="rounded" width={80} height={5} sx={{ borderRadius: 2.5 }} />
                </Box>
              ))}
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Card elevation={0} sx={{ height: '100%', ...CARD_SX }}>
              <Box sx={{ ...CARD_HEADER_SX }}><Skeleton width={160} height={16} /></Box>
              <Box sx={{ display: 'flex', flex: 1 }}>
                {[0, 1].map(side => (
                  <Box key={side} sx={{ flex: 1, p: 1.5 }}>
                    <Skeleton width={80} height={14} sx={{ mb: 1 }} />
                    {[0, 1, 2, 3, 4].map(j => (
                      <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Skeleton width={12} height={11} />
                        <Skeleton width={40} height={14} sx={{ flex: 1 }} />
                        <Skeleton width={32} height={12} />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Card>
          </Box>
        </Box>

        {/* Charts */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Card elevation={0} sx={{ ...CARD_SX, height: '100%' }}>
              <Box sx={{ ...CARD_HEADER_SX }}><Skeleton width={140} height={16} /></Box>
              <Box sx={{ p: 2 }}><Skeleton variant="rounded" height={200} sx={{ borderRadius: 1.5 }} /></Box>
            </Card>
          </Box>
          <Box sx={{ flex: '0 0 38%', minWidth: 0 }}>
            <Card elevation={0} sx={{ ...CARD_SX, height: '100%' }}>
              <Box sx={{ ...CARD_HEADER_SX }}><Skeleton width={100} height={16} /></Box>
              <Box sx={{ p: 2 }}><Skeleton variant="rounded" height={200} sx={{ borderRadius: 1.5 }} /></Box>
            </Card>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER — Consistent with other pages
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MonitorHeartIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('concentrationAllocation.pageTitle')}
              </Typography>
              {report?.allocation?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {report.allocation.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('concentrationAllocation.pageSubtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {t('concentrationAllocation.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
          <Button size="small" onClick={fetchData} sx={{ ml: 2 }}>{t('concentrationAllocation.retry')}</Button>
        </Alert>
      )}

      {calculating && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t('concentrationAllocation.calculating')}
        </Alert>
      )}

      {/* Hero */}
      <HeroCard data={latest} regimeDays={regimeDays} prevRegime={prevRegime} />

      {/* Row 2 — Score Strip */}
      <ScoreStrip data={latest} prevData={prevData} />

      {/* Row 3 — Buckets + Contributors (height-balanced) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 45%' }, minWidth: 0, display: 'flex' }}>
          <BucketCard data={latest} />
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0, display: 'flex' }}>
          <ContributorsCard data={latest} />
        </Box>
      </Box>

      {/* Row 3b — Sector Summary */}
      <SectorStrip data={latest} />

      {/* Row 4 — Chart header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
          {t('concentrationAllocation.chart.lookback', { days: historyDays })}
        </Typography>
        <ToggleButtonGroup value={historyDays} exclusive size="small"
          onChange={(e, val) => val && setHistoryDays(val)}>
          {[7, 14, 30, 60, 90, 180, 365].map(d => (
            <ToggleButton key={d} value={d} sx={{ fontSize: '0.68rem', px: 1.25, py: 0.25 }}>
              {d}d
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Row 5 — Charts */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AllocationChart history={history} />
        <SignalsChart history={history} />
      </Box>
    </Box>
  );
}
